/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import {useForm} from 'react-hook-form'

import PropTypes from 'prop-types'

import {AuthHelpers, useAuthHelper, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Box,
    Container,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'
import {
    AuthModal,
    PASSWORD_VIEW,
    useAuthModal
} from '@epmc-mach/accelarator/overrides/app/hooks/use-auth-modal'
import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-customer'
import useFormPersistence from '@epmc-mach/accelarator/overrides/app/hooks/use-form-persistence'

import {useCartProducts} from '../../../components/order-summary/hooks/useCartProducts'
import {trackEvent} from '../../../utils/gtm'
import checkCustomerByEmailHelper from '../utils/check-customer-by-email'
import {useCheckout} from '../utils/checkout-context'
import {useCheckoutAddresses} from './checkout-addresses-context'
import Button from '/overrides/app/components/button'
import Field from '/overrides/app/components/field'
import useLoginFields from '/overrides/app/components/forms/useLoginFields'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '/overrides/app/components/toggle-card'
import {legacyNavigator} from '/overrides/app/utils/navigator'

const ContactInfo = () => {
    const {app: appConfig} = getConfig()
    const {featureFlags} = appConfig
    const {basket, customer, resetAndReinitialize} = useCheckoutAddresses()
    const {getTokenWhenReady} = useAccessToken()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const logout = useAuthHelper(AuthHelpers.Logout)
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')
    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const {site} = useMultiSite()

    const {refetch: refetchCustomer} = useCurrentCustomer()
    const {refetch: refetchBasket, isMergingInProgress, setIsMergingInProgress} = useCurrentBasket()

    const {step, STEPS, goToStep, goToNextStep} = useCheckout()

    const {products} = useCartProducts(basket)

    const populateProductData = () => {
        return basket?.productItems
            ?.map((product) => {
                return {...products[product.productId], quantity: product?.quantity}
            })
            ?.map((product, idx) => {
                return {
                    name: product?.c_customProductName,
                    id: product?.c_articleNumber,
                    price: product?.price,
                    brand: product?.manufacturerName,
                    list: 'checkout',
                    variant: product?.c_color,
                    category: product?.c_custom?.categoryTree,
                    quantity: product?.quantity,
                    dimension5: product?.c_size + '/' + product?.c_sizeUK,
                    position: idx
                }
            })
    }

    const defaultValues = {
        email: customer?.email || basket?.customerInfo?.email || '',
        password: ''
    }
    const {tCheckoutPage, tContactInfo} = useTranslationHelpers()

    const form = useForm({
        defaultValues
    })

    const {clearFormData} = useFormPersistence(
        'contact-info',
        form.watch,
        form.reset,
        defaultValues
    )

    const fields = useLoginFields({form})
    const emailRef = useRef()

    const [error, setError] = useState(null)
    const [showPasswordField, setShowPasswordField] = useState(false)
    const [signOutConfirmDialogIsOpen, setSignOutConfirmDialogIsOpen] = useState(false)
    const [continueButtonClicked, setContinueButtonClicked] = useState(false)

    const [authModalView, setAuthModalView] = useState(PASSWORD_VIEW)
    const authModal = useAuthModal(authModalView)

    const checkCustomerByEmail = async (email) => {
        try {
            if (featureFlags?.enableSkipLoginOnCheckout) {
                return false
            }

            const token = await getTokenWhenReady()
            return await checkCustomerByEmailHelper(email, site?.id, token)
        } catch (error) {
            console.error('Error fetching promotions:', error)
            return false
        }
    }

    const submitForm = async (data) => {
        if (continueButtonClicked) {
            onContinueClick()
            return
        }
        setError(null)
        form.clearErrors('password') // Clear previous password errors
        try {
            if (data.password && data.email) {
                setIsMergingInProgress(true)

                await login.mutateAsync({
                    username: data.email,
                    password: data.password
                })

                const hasBasketItem = basket.productItems?.length > 0
                if (hasBasketItem) {
                    await mergeBasket.mutateAsync({
                        parameters: {
                            createDestinationBasket: true
                        }
                    })
                }

                await Promise.all([refetchCustomer(), refetchBasket()])
                resetAndReinitialize()

                setIsMergingInProgress(false)
            }
            clearFormData()
            goToNextStep()
            trackEvent('checkout', {
                ecommerce: {
                    checkout: {
                        actionField: {
                            step: 2,
                            option: '',
                            action: 'checkout'
                        },
                        products: populateProductData()
                    }
                }
            })
        } catch (error) {
            setIsMergingInProgress(false)

            // Check for authentication error (401 status) - handles both client and server environments
            const isAuthError =
                /Unauthorized/i.test(error.message) || error.response?.status === 401

            if (isAuthError) {
                // Set error directly on password field
                form.setError('password', {
                    type: 'manual',
                    message: tContactInfo?.emailValidationError
                })
            } else {
                setError(error.message)
            }
        }
    }

    const togglePasswordField = async () => {
        if (error) {
            setError(null)
        }
        form.clearErrors('password') // Clear password errors when toggling
        setShowPasswordField(!showPasswordField)
        if (emailRef.current) {
            emailRef.current.focus()
        }
    }

    const onForgotPasswordClick = () => {
        setAuthModalView(PASSWORD_VIEW)
        authModal.onOpen()
    }

    const onCheckoutAsGuestClick = async () => {
        setError(null)

        // Trigger email field validation
        const isEmailValid = await form.trigger('email')
        if (!isEmailValid) {
            return // Stop if email validation fails
        }

        var email = form.watch('email')
        try {
            if (email) {
                if (basket?.customerInfo?.email !== email) {
                    await updateCustomerForBasket.mutateAsync({
                        parameters: {basketId: basket.basketId},
                        body: {email}
                    })
                }

                clearFormData()
                goToNextStep()
                trackEvent('checkout', {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 2,
                                option: '',
                                action: 'checkout'
                            },
                            products: populateProductData()
                        }
                    }
                })
            }
        } catch (error) {
            setError(error.message)
        }
    }

    const handleEmailBlur = (email) => {
        const currentBasketEmail = basket?.customerInfo?.email
        const trimmedEmail = email?.trim()

        if (trimmedEmail && trimmedEmail !== currentBasketEmail && step !== STEPS.CONTACT_INFO) {
            goToStep(STEPS.CONTACT_INFO)
        }
    }

    const onContinueClick = async () => {
        setError(null)
        setContinueButtonClicked(false)

        // Trigger email field validation
        const isEmailValid = await form.trigger('email')
        if (!isEmailValid) {
            return // Stop if email validation fails
        }

        try {
            const customerHasAccount = await checkCustomerByEmail(emailRef.current?.value)
            if (customerHasAccount) {
                togglePasswordField()
            } else {
                onCheckoutAsGuestClick()
            }
        } catch (error) {
            setError(error.message)
        }
    }

    useEffect(() => {
        if (!showPasswordField) {
            form.unregister('password')
        }
    }, [showPasswordField])

    useEffect(() => {
        if (!basket?.customerInfo?.email && !customer?.email) {
            goToStep(STEPS.CONTACT_INFO)
        }
    }, [])

    useEffect(() => {
        const savedData = sessionStorage.getItem('checkout_form_contact-info')
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData)
                const savedEmail = parsedData.email?.trim()
                const basketEmail = basket?.customerInfo?.email

                if (
                    savedEmail &&
                    savedEmail !== basketEmail &&
                    step !== STEPS.CONTACT_INFO &&
                    customer?.isGuest
                ) {
                    goToStep(STEPS.CONTACT_INFO)
                }
            } catch (error) {
                console.warn('Failed to parse saved form data:', error)
            }
        }
    }, [basket?.customerInfo?.email, step, customer?.isGuest])

    return (
        <ToggleCard
            id="step-0"
            title={tCheckoutPage?.step1Title}
            editing={step === STEPS.CONTACT_INFO}
            isLoading={form.formState.isSubmitting || isMergingInProgress}
            onEdit={() => {
                if (customer.isRegistered) {
                    setSignOutConfirmDialogIsOpen(true)
                } else {
                    goToStep(STEPS.CONTACT_INFO)
                }
            }}
            editLabel={customer.isRegistered ? tContactInfo?.signOut : tContactInfo?.editInfo}
        >
            <ToggleCardEdit>
                <Container variant="form" maxW="465px">
                    <form onSubmit={form.handleSubmit(submitForm)} noValidate>
                        <Stack spacing={6}>
                            {error && !form.formState.errors.password && (
                                <Alert status="error">
                                    <AlertIcon />
                                    {error}
                                </Alert>
                            )}

                            <Stack spacing={5} position="relative">
                                <Box fontWeight="700" letterSpacing="0" fontSize="13.5px">
                                    <Field
                                        {...fields.email}
                                        placeholder={tContactInfo?.emailPlaceholder}
                                        formLabel={tContactInfo?.emailFormLabel}
                                        inputRef={emailRef}
                                        onBlur={(e) => handleEmailBlur(e.target.value)}
                                    />
                                </Box>
                                {showPasswordField && (
                                    <Stack>
                                        <Box fontWeight="700" letterSpacing="0" fontSize="13.5px">
                                            <Field
                                                {...fields.password}
                                                formLabel={tContactInfo?.passwordFormLabel}
                                            />
                                        </Box>
                                        <Box>
                                            <Button
                                                variant="link"
                                                size="md"
                                                color="gray.300"
                                                textDecoration="underline"
                                                onClick={onForgotPasswordClick}
                                            >
                                                {tContactInfo?.forgotPassword}
                                            </Button>
                                        </Box>
                                    </Stack>
                                )}
                            </Stack>

                            <Stack spacing={3}>
                                {showPasswordField && (
                                    <Button
                                        type="submit"
                                        textTransform="uppercase"
                                        backgroundColor="gray.600"
                                    >
                                        {tContactInfo?.login}
                                    </Button>
                                )}

                                {!showPasswordField ? (
                                    <Button
                                        type="submit"
                                        variant="solid"
                                        borderColor="gray.500"
                                        textTransform="uppercase"
                                        onClick={() => setContinueButtonClicked(true)}
                                    >
                                        {tContactInfo?.buttonContinue}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline"
                                        borderColor="gray.500"
                                        textTransform="uppercase"
                                        fontWeight="normal"
                                        onClick={onCheckoutAsGuestClick}
                                    >
                                        {tContactInfo?.loginGuest}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </form>
                </Container>
                <AuthModal
                    {...authModal}
                    initialEmail={form.getValues().email}
                    onContinueAsGuest={onCheckoutAsGuestClick}
                />
            </ToggleCardEdit>
            <ToggleCardSummary>
                <Text>{basket?.customerInfo?.email || customer?.email}</Text>

                <SignOutConfirmationDialog
                    isOpen={signOutConfirmDialogIsOpen}
                    onClose={() => setSignOutConfirmDialogIsOpen(false)}
                    onConfirm={async () => {
                        await logout.mutateAsync()
                        legacyNavigator.navigateToLoginPage()
                        setSignOutConfirmDialogIsOpen(false)
                    }}
                />
            </ToggleCardSummary>
        </ToggleCard>
    )
}

ContactInfo.propTypes = {
    isSocialEnabled: PropTypes.bool,
    isPasswordlessEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string)
}

const SignOutConfirmationDialog = ({isOpen, onConfirm, onClose}) => {
    const cancelRef = useRef()
    const {tContactInfo} = useTranslationHelpers()

    return (
        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="xxl" fontWeight="bold" color="black">
                        {tContactInfo?.signOutTitle}
                    </AlertDialogHeader>

                    <AlertDialogBody fontSize="lg" color="gray.300">
                        {tContactInfo?.signOutMessage}
                    </AlertDialogBody>

                    <AlertDialogFooter>
                        <Button
                            ref={cancelRef}
                            variant="outline"
                            textTransform="uppercase"
                            onClick={onClose}
                        >
                            {tContactInfo?.signOutCancelButton}
                        </Button>
                        <Button
                            colorScheme="red"
                            textTransform="uppercase"
                            onClick={onConfirm}
                            ml={3}
                        >
                            {tContactInfo?.signOutConfirmButton}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    )
}

SignOutConfirmationDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func
}

export default ContactInfo
